import axios from "@/apis/custom/axiosConfig";
import { BACKEND_URL } from "@/lib/constants/constants";
import * as Sentry from "@sentry/react";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/*******************************************************
 * Types
 *******************************************************/
interface RealtimeContextValue {
  sendRequest: (prompt: string) => Promise<any>;
  isInitialized: boolean;
  error: any;
}

export const RealtimeContext = createContext<RealtimeContextValue>({
  sendRequest: async () => {
    throw new Error("RealtimeContext not initialized");
  },
  isInitialized: false,
  error: null,
});

/*******************************************************
 * The Provider - Lazy Initialization
 * WebRTC connection is only established when sendRequest is called
 *******************************************************/
export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const ephemeralKeyRef = useRef<string | null>(null);

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  // Track mounted status to avoid setState after unmount
  const isMountedRef = useRef<boolean>(true);
  // Suppress event-driven error handling during intentional shutdowns
  const suppressEventsRef = useRef<boolean>(false);

  const handleError = useCallback(
    (err: any, options?: { level?: "info" | "error" }) => {
      try {
        Sentry.captureException(err as any, {
          level: options?.level || "error",
        });
      } catch (_) {
        // ignore
      }
      console.error(err);
      if (!isMountedRef.current) {
        return;
      }
      setError(err);
    },
    [],
  );

  // A promise that resolves once the DataChannel is fully open
  const dataChannelOpenPromiseRef = useRef<Promise<void> | null>(null);
  const dataChannelOpenResolveRef = useRef<(() => void) | null>(null);
  const initPromiseRef = useRef<Promise<void> | null>(null);

  const pendingRequestsRef = useRef<
    Map<
      string,
      {
        resolve: (value: any) => void;
        reject: (reason?: any) => void;
        responseId?: string; // filled in once we see "response.created"
      }
    >
  >(new Map());

  const responseToRequestRef = useRef<Map<string, string>>(new Map());

  // Track last activity for session management
  const lastActivityRef = useRef<number>(0);
  const reinitIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /*******************************************************
   * Handle incoming messages
   *******************************************************/
  const handleIncomingMessage = useCallback(
    (event: MessageEvent) => {
      try {
        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch (err) {
          console.warn("Received non-JSON message:", event.data);
          return;
        }

        // data.type can be "response.created", "response.output_item.*", etc
        const eventType = data.type;

        if (eventType === "response.created") {
          const requestId = data?.response?.metadata?.requestId;
          const responseId = data?.response?.id;
          if (!requestId || !responseId) {
            console.warn("response.created missing requestId or responseId");
            return;
          }

          // Find the request record
          const pending = pendingRequestsRef.current.get(requestId);
          if (!pending) {
            console.warn("No pending request for requestId:", requestId);
            return;
          }

          pending.responseId = responseId;

          responseToRequestRef.current.set(responseId, requestId);

          return;
        }

        if (eventType?.startsWith("response.output_item")) {
          // e.g., response.output_item.done, response.output_item.partial, etc
          const responseId = data.response_id;
          if (!responseId) {
            console.warn("No response_id in output_item event:", data);
            return;
          }

          const requestId = responseToRequestRef.current.get(responseId);
          if (!requestId) {
            console.warn(`No requestId mapped for responseId ${responseId}`);
            return;
          }

          const pending = pendingRequestsRef.current.get(requestId);
          if (!pending) {
            console.warn(
              "No pending request found for requestId (output_item):",
              requestId,
            );
            return;
          }

          if (eventType === "response.output_item.done") {
            console.log("Received final response for requestId:", requestId);

            pending.resolve(data);

            // Clean up
            responseToRequestRef.current.delete(responseId);
            pendingRequestsRef.current.delete(requestId);
          } else {
            // ignore partials
          }
          return;
        }
      } catch (err) {
        handleError(err);
      }
    },
    [handleError],
  );

  /**
   * Main init function. Called lazily when sendRequest is first called.
   */
  const initializeRealtimeSession = useCallback(async () => {
    // If a previous init is in progress or done, re-use the same promise
    if (initPromiseRef.current) {
      return initPromiseRef.current;
    }

    initPromiseRef.current = (async () => {
      const MAX_RETRIES = 5;
      const BASE_DELAY_MS = 300;

      let attempt = 0;
      let succeeded = false;

      while (attempt < MAX_RETRIES && !succeeded) {
        try {
          const tokenResp = await axios.get(`${BACKEND_URL}/open-ai/session`);

          const ephemeralKey = tokenResp.data?.client_secret?.value;
          if (!ephemeralKey) {
            throw new Error("No ephemeral key returned by backend");
          }
          ephemeralKeyRef.current = ephemeralKey;

          const pc = new RTCPeerConnection();
          pcRef.current = pc;

          // Must add audio track for the real-time API
          pc.addTransceiver("audio", { direction: "sendonly" });

          const dc = pc.createDataChannel("oai-events");
          dcRef.current = dc;

          dataChannelOpenPromiseRef.current = new Promise<void>((resolve) => {
            dataChannelOpenResolveRef.current = resolve;
          });

          dc.addEventListener("open", () => {
            console.log("DataChannel is open");
            dataChannelOpenResolveRef.current?.();
          });
          dc.addEventListener("message", handleIncomingMessage);
          dc.addEventListener("error", (e) => {
            if (suppressEventsRef.current) return;
            handleError(e);
          });
          dc.addEventListener("close", () => {
            if (suppressEventsRef.current) return;
            handleError(new Error("DataChannel closed"));
          });

          // Monitor peer connection state for failures
          pc.addEventListener("connectionstatechange", () => {
            const state = pc.connectionState;
            if (state === "failed") {
              if (suppressEventsRef.current) return;
              handleError(new Error("RTCPeerConnection state failed"), {
                level: "info",
              });
            }
          });
          pc.addEventListener("iceconnectionstatechange", () => {
            const state = pc.iceConnectionState;
            if (state === "failed") {
              if (suppressEventsRef.current) return;
              handleError(new Error("ICE connection failed"));
            }
          });

          // New session is being established, start considering errors again
          suppressEventsRef.current = false;

          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);

          const baseUrl = "https://api.openai.com/v1/realtime";

          const sdpResponse = await fetch(`${baseUrl}`, {
            method: "POST",
            body: offer.sdp,
            headers: {
              Authorization: `Bearer ${ephemeralKey}`,
              "Content-Type": "application/sdp",
            },
          });

          if (!sdpResponse.ok) {
            throw new Error(
              `Realtime SDP exchange failed: ${sdpResponse.status} ${sdpResponse.statusText}`,
            );
          }

          const answer = {
            type: "answer",
            sdp: await sdpResponse.text(),
          };
          await pc.setRemoteDescription(answer as RTCSessionDescriptionInit);

          succeeded = true;
          lastActivityRef.current = Date.now();

          if (isMountedRef.current) {
            setIsInitialized(true);
            setError(null);
          }

          // Start re-init interval only after first successful connection
          startReinitInterval();
        } catch (err) {
          try {
            Sentry.captureException(err as any);
          } catch (_) {}
          if (isMountedRef.current) {
            setError(err);
          }
          attempt += 1;
          if (attempt < MAX_RETRIES) {
            const delayMs = BASE_DELAY_MS * 2 ** (attempt - 1);
            await delay(delayMs);
          }
        }
      }

      if (!succeeded) {
        initPromiseRef.current = null;
        if (isMountedRef.current) {
          setIsInitialized(false);
        }
      }
    })();

    return initPromiseRef.current;
  }, [handleError, handleIncomingMessage]);

  /*******************************************************
   * Cleanup
   *******************************************************/
  const cleanupSession = useCallback(() => {
    // Suppress event-driven error handling during intentional shutdowns
    suppressEventsRef.current = true;

    const dc = dcRef.current;
    const pc = pcRef.current;
    if (dc) {
      try {
        dc.close();
      } catch (e) {}
    }
    if (pc) {
      try {
        pc.close();
      } catch (e) {}
    }
    pcRef.current = null;
    dcRef.current = null;
    dataChannelOpenPromiseRef.current = null;
    dataChannelOpenResolveRef.current = null;
    initPromiseRef.current = null;
    ephemeralKeyRef.current = null;
    if (isMountedRef.current) {
      setIsInitialized(false);
    }
  }, []);

  /*******************************************************
   * Re-init every 5 minutes if no outstanding requests
   * Only starts after first connection
   *******************************************************/
  const startReinitInterval = useCallback(() => {
    // Clear any existing interval
    if (reinitIntervalRef.current) {
      clearInterval(reinitIntervalRef.current);
    }

    reinitIntervalRef.current = setInterval(
      () => {
        // Only re-initialize if there are no pending requests
        if (pendingRequestsRef.current.size === 0) {
          console.log("No outstanding requests; re-initializing session...");
          cleanupSession();
          initializeRealtimeSession().catch(handleError);
        } else {
          console.log("Skipping re-init; there are outstanding requests.");
        }
      },
      5 * 60 * 1000,
    ); // 5 minutes in milliseconds
  }, [cleanupSession, initializeRealtimeSession, handleError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      suppressEventsRef.current = true;
      if (reinitIntervalRef.current) {
        clearInterval(reinitIntervalRef.current);
      }
      cleanupSession();
    };
  }, [cleanupSession]);

  /*******************************************************
   * sendRequest - Lazy initialization happens here
   *******************************************************/
  const sendRequest = useCallback(
    async (prompt: string): Promise<any> => {
      try {
        // Lazy initialization: only connect when actually needed
        if (!initPromiseRef.current) {
          await initializeRealtimeSession();
        } else {
          await initPromiseRef.current;
        }

        // Wait for data channel to open
        if (dataChannelOpenPromiseRef.current) {
          await dataChannelOpenPromiseRef.current;
        }

        const dc = dcRef.current;
        if (!dc || dc.readyState !== "open") {
          throw new Error(
            "DataChannel is not open. Wait for it to open before sending requests.",
          );
        }

        // Update last activity time
        lastActivityRef.current = Date.now();

        const requestId = generateRequestId();

        const payload = {
          type: "response.create",
          response: {
            conversation: "none",

            metadata: {
              requestId,
            },
            modalities: ["text"],

            instructions: prompt,
          },
        };

        // Return a promise that will resolve on message response
        const promise = new Promise<any>((resolve, reject) => {
          pendingRequestsRef.current.set(requestId, { resolve, reject });
        });

        dc.send(JSON.stringify(payload));
        const res = await promise;
        return res;
      } catch (err) {
        handleError(err);
        // allow this to throw so callers can try/catch
        throw err;
      }
    },
    [handleError, initializeRealtimeSession],
  );

  /*******************************************************
   * Context Value
   *******************************************************/
  const value: RealtimeContextValue = {
    sendRequest,
    isInitialized,
    error,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

/*******************************************************
 * Utility
 *******************************************************/
function generateRequestId() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

function delay(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error("useRealtime must be used within a RealtimeProvider");
  }
  return context;
}
